// Import statements
import {
  DiscountAuditSnapshot,
  DiscountType,
  formatCurrency,
  StoreProductChange,
  StoreProductChangeEvents,
} from '@bofrak-backend/shared';
import {
  Alert,
  AlertIcon,
  Box,
  chakra,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from '@chakra-ui/react';
import moment from 'moment';
import React from 'react';
import { useSharedUiTranslation } from '../../i18n';

// Custom table header component
const CustomTh = chakra('th', {
  baseStyle: {
    borderBottom: '1px solid',
    borderColor: 'gray.300',
  },
});

// TableRow component
const TableRow: React.FC<StoreProductChange> = ({
  created_at,
  updator_name,
  changed_attributes,
  product_name,
  event,
  change_reason,
}) => {
  const { t } = useSharedUiTranslation();
  const isDiscountEvent = [
    StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_ASSIGNED,
    StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_REASSIGNED,
    StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UNASSIGNED,
    StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UPDATED,
    StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_EXPIRED,
    StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_DELETED,
    StoreProductChangeEvents.STORE_PRODUCT_DISCOUNTED_PRICE_UPDATED,
  ].includes(event);

  if (isDiscountEvent) {
    const discountChange = changed_attributes.discount;
    const priceChange = changed_attributes.discounted_price;
    const formatDiscount = (value: unknown, price: unknown) => {
      const discount = value as DiscountAuditSnapshot | null;
      if (!discount) return t('No discount');
      const rate =
        discount.type === DiscountType.FIXED_PERCENT
          ? `${discount.discount_percent}%`
          : formatCurrency(discount.discount_amount ?? 0, false);
      const effectivePrice =
        typeof price === 'number' ? formatCurrency(price, false) : '—';
      return `${discount.name} · ${rate} · ${effectivePrice}`;
    };
    const discountEventLabels: Partial<Record<string, string>> = {
      [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_ASSIGNED]:
        t('Discount Assigned'),
      [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_REASSIGNED]: t(
        'Discount Reassigned',
      ),
      [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UNASSIGNED]: t(
        'Discount Unassigned',
      ),
      [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UPDATED]:
        t('Discount Updated'),
      [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_EXPIRED]:
        t('Discount Expired'),
      [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_DELETED]:
        t('Discount Deleted'),
      [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNTED_PRICE_UPDATED]: t(
        'Discounted Price Updated',
      ),
    };
    const label =
      discountEventLabels[event] ??
      event.replace('STORE_PRODUCT_', '').replace(/_/g, ' ').toLowerCase();
    return (
      <Tr fontSize="sm">
        <Td>
          <Text pt={1} fontWeight="bold" color="purple.600" fontSize="xs">
            {capitalizeWords(product_name ?? '')}
          </Text>
          <Text fontWeight="bold" fontSize="xs" textTransform="capitalize">
            {label}
          </Text>
          <Text fontSize="xs">
            {capitalizeWords(updator_name) ?? t('Unknown').toUpperCase()}
          </Text>
          {change_reason && <Text fontSize="xs">{change_reason}</Text>}
        </Td>
        <Td>
          <Text textAlign="center" fontSize="sm" color="purple.700">
            {formatDiscount(discountChange?.old, priceChange?.old)}
          </Text>
        </Td>
        <Td>
          <Text fontSize="sm" color="purple.700" fontWeight="bold">
            {formatDiscount(discountChange?.new, priceChange?.new)}
          </Text>
          <Text fontSize="xs">
            {moment(created_at).format('DD.MM.YY @ HH:mm')}
          </Text>
        </Td>
      </Tr>
    );
  }

  const keyOfChange = Object.keys(changed_attributes)[0];
  const _old = changed_attributes[keyOfChange].old;
  const _new = changed_attributes[keyOfChange].new;

  const diff = _new - _old;

  let oldValue = _old;
  let newValue = _new;

  let isCurrency = false;

  if (
    ['price', 'cost', 'max_selling_price', 'min_selling_price'].includes(
      keyOfChange,
    )
  ) {
    oldValue = formatCurrency(oldValue, false);
    newValue = formatCurrency(newValue, false);
    isCurrency = true;
  }

  // Labels for the attributes recorded in store product change history;
  // unknown attributes fall back to the raw (humanised) attribute name.
  const attributeLabels: Partial<Record<string, string>> = {
    allow_below_cost_sale: t('Allow Below Cost Sale'),
    cost: t('Cost'),
    inventory: t('Inventory'),
    is_available_for_sale: t('Is Available For Sale'),
    is_available_in_store: t('Is Available In Store'),
    is_manually_controlled: t('Is Manually Controlled'),
    is_sold_by_range: t('Is Sold By Range'),
    is_sold_by_weight: t('Is Sold By Weight'),
    is_sold_online: t('Is Sold Online'),
    low_stock_threshold: t('Low Stock Threshold'),
    loyalty_programs: t('Loyalty Programs'),
    max_selling_price: t('Max Selling Price'),
    min_selling_price: t('Min Selling Price'),
    popularity_score: t('Popularity Score'),
    price: t('Price'),
    should_display_cost_on_pos: t('Should Display Cost On Pos'),
  };

  const fomattedKeyOfChange =
    attributeLabels[keyOfChange] ?? keyOfChange.replace(/_/g, ' ');

  return (
    <Tr fontSize={'sm'}>
      {/* Date and User */}
      <Td>
        <Box>
          <Text pt={1} fontWeight={'bold'} color={'blue'} fontSize={'xs'}>
            {capitalizeWords(product_name ?? '') ?? ''}
          </Text>
          <Text fontWeight={'bold'} fontSize={'xs'}>
            {capitalizeWords(fomattedKeyOfChange) ?? t('Unknown').toUpperCase()}
          </Text>

          <Text fontSize={'xs'}>
            {capitalizeWords(updator_name) ?? t('Unknown').toUpperCase()}
          </Text>
        </Box>
      </Td>

      {/* Old Value */}
      <Td>
        <Text textAlign={'center'} fontSize={'sm'}>
          {oldValue}
        </Text>
      </Td>

      {/* New Value */}
      <Td>
        <Box>
          <Text fontSize={'sm'}>{newValue}</Text>
          {isCurrency && (
            <Text
              color={diff > 0 ? 'green.500' : diff < 0 ? 'red.500' : 'gray.500'}
              fontSize={'xs'}>
              {formatCurrency(diff, false)}
            </Text>
          )}
          <Text fontSize={'xs'}>
            {moment(created_at).format('DD.MM.YY @ HH:mm')}
          </Text>
        </Box>
      </Td>
    </Tr>
  );
};

const capitalizeWords = (str: string) =>
  str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const ProductHistoryTable: React.FC<{
  data: StoreProductChange[];
}> = ({ data }) => {
  const { t } = useSharedUiTranslation();
  return data.length ? (
    <TableContainer width="full">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr fontSize={'sm'}>
            <CustomTh></CustomTh>
            <CustomTh>{t('Old Value')}</CustomTh>
            <CustomTh>{t('New Value')}</CustomTh>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, i) => (
            <TableRow key={row.id + i} {...row} />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  ) : (
    <Alert textTransform={'capitalize'} width={'auto'} status="info">
      <AlertIcon />
      {t('No Product History Found')}
    </Alert>
  );
};
